import { usePrimaryColor } from '../../branding/usePrimaryColor';
import type { UpcomingPreviewDay } from '../../types/dashboard';

interface UpcomingPreviewProps {
  days: UpcomingPreviewDay[];
  totalCount: number;
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  onRetry?: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatDayLabel(dateStr: string): { dayName: string; dateLabel: string } {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate();

  return {
    dayName: isTomorrow ? 'Tomorrow' : DAY_NAMES[d.getDay()],
    dateLabel: `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`,
  };
}

export function UpcomingPreview({
  days,
  totalCount,
  loading = false,
  error = false,
  forbidden = false,
  onRetry,
}: UpcomingPreviewProps) {
  const primaryColor = usePrimaryColor();

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Week ahead</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Week ahead</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load upcoming sessions.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && days.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-24 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-2.5">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="h-3 w-20 bg-canvas animate-pulse rounded" />
              <div className="h-3 w-10 bg-canvas animate-pulse rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state: no sessions upcoming
  if (totalCount === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Week ahead</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No sessions booked for the next 7 days.
          </p>
        </div>
      </div>
    );
  }

  const maxTotal = Math.max(...days.map((d) => d.total), 1);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-semibold">Week ahead</h3>
          <span className="text-[11.5px] text-ink-4 num">
            {totalCount} session{totalCount !== 1 ? 's' : ''} upcoming
          </span>
        </div>
      </div>

      {/* Day rows */}
      <div className="px-4 pt-3 pb-3 space-y-1.5">
        {days.map((day) => {
          const { dayName, dateLabel } = formatDayLabel(day.date);

          return (
            <div key={day.date} className="flex items-center gap-2">
              {/* Day name */}
              <span className="text-[11.5px] text-ink-3 w-[60px] truncate shrink-0">
                {dayName}
              </span>
              {/* Date */}
              <span className="text-[10.5px] text-ink-4 w-10 shrink-0">
                {dateLabel}
              </span>
              {/* Bar */}
              <div className="flex-1 h-5 rounded overflow-hidden bg-canvas relative">
                {day.total > 0 && (
                  <>
                    {/* Confirmed portion */}
                    <div
                      className="h-full rounded absolute left-0 top-0 transition-all duration-300"
                      style={{
                        width: `${(day.confirmed / maxTotal) * 100}%`,
                        backgroundColor: primaryColor,
                        opacity: 0.8,
                      }}
                    />
                    {/* Unconfirmed portion (stacked after confirmed) */}
                    {day.unconfirmed > 0 && (
                      <div
                        className="h-full rounded absolute top-0 transition-all duration-300"
                        style={{
                          left: `${(day.confirmed / maxTotal) * 100}%`,
                          width: `${(day.unconfirmed / maxTotal) * 100}%`,
                          backgroundColor: primaryColor,
                          opacity: 0.3,
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              {/* Count */}
              <span className="text-[11.5px] num shrink-0 w-8 text-right font-medium">
                {day.total > 0 ? day.total : '–'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="px-4 pb-3 flex items-center gap-4 text-[10.5px] text-ink-4">
        <span className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-sm"
            style={{ backgroundColor: primaryColor, opacity: 0.8 }}
          />
          Confirmed
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-sm"
            style={{ backgroundColor: primaryColor, opacity: 0.3 }}
          />
          Unconfirmed
        </span>
      </div>
    </div>
  );
}
